import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, nextTick, watch } from 'vue'

import { persistFocusedItem } from '@app/application/workflows/screens/workflow-review/api/persist-focused-item'
import { constructItemNavigationUrl } from '@app/application/workflows/utils/construct-item-navigation-url'
import { SfLog } from '@app/adapters/logger'

import { useWorfklowReviewItems } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-items'

export function usePersistFocusedItem({
  ledgerId,
  workflowId,
}: {
  ledgerId: number
  workflowId: number
}): void {
  const workflowReviewItemsStore = useWorfklowReviewItems()

  const { currentReviewItem } = storeToRefs(workflowReviewItemsStore)

  const prepareTabUrl = computed(() => {
    if (currentReviewItem.value) {
      const path = constructItemNavigationUrl({
        itemId:
          currentReviewItem.value.itemClass === 'account_entry'
            ? currentReviewItem.value.contextHrefParam
            : currentReviewItem.value.id,
        ledgerId,
        type: currentReviewItem.value.itemClass,
      })

      return path
    }

    return ''
  })

  async function handleTurboVisit(
    e: Event & {
      data?: { url: string }
    }
  ): Promise<void> {
    e.preventDefault()
    e.stopPropagation()

    const target = e.target as HTMLAnchorElement

    if (currentReviewItem.value) {
      try {
        await persistFocusedItem({
          ledgerId,
          itemId: currentReviewItem.value.id,
          itemType: currentReviewItem.value.itemClass,
          accountNumber: currentReviewItem.value.contextHrefParam ?? '',
          workflowId,
        })
      } catch (e) {
        SfLog.warn('Error updating prepare tab url', {
          extra: {
            error: e,
          },
        })
      }
    }

    const link = e?.data?.url ?? target.href

    window.Turbolinks.visit(link)
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  window.addEventListener('turbolinks:before-visit', handleTurboVisit, {
    once: true,
  })

  onBeforeUnmount(() => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.removeEventListener('turbolinks:before-visit', handleTurboVisit)
  })

  watch(
    prepareTabUrl,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        void nextTick(() => {
          window.eventHub.$emit('prepare-tab-url-updated', newValue)
        })
      }
    },
    { immediate: true }
  )
}
